'use client';
import { Testimonials } from '@bw/apis/contentful';
import { cn } from '@bw/core';
import { Image } from '@bw/elements';
import { LogoVector, Quote } from '@bw/elements/icons';
import { FC } from 'react';
import 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/mousewheel';
import 'swiper/css/scrollbar';
import { A11y, Autoplay, Mousewheel, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { annotate } from '../../utils/annotate';
import { assetUri } from '../../utils/assetUri';
import { schemeToClass } from '../../utils/colorMap';
import { defined } from '../../utils/defined';
import { RichText } from '../../utils/rich-text/RichText';
import styles from './Testimonials.module.scss';

export const TestimonialsClient: FC<Testimonials<"WITHOUT_UNRESOLVABLE_LINKS">> = ({
	sys: { id },
	fields: {
		title,
		items,
		colorScheme = 'Default (white)',
	}
}) =>
	<section className={cn(styles.testimonials, schemeToClass(colorScheme))} {...annotate(id, '.')}>
		<div className={styles.container}>
			<div className={styles.header}>
				<article>
					<h2 data-aos="fade-up">{title}</h2>
				</article>
				<figure data-aos="fade-up">
					<Quote width={107} height={80} />
				</figure>
			</div>
		</div>
		<div className={styles.wrapper}>
			<Swiper
				modules={[Autoplay, Scrollbar, A11y, Mousewheel]}
				autoplay={{ delay: 20, disableOnInteraction: true }}
				loop={true}
				speed={10000}
				draggable={true}
				breakpoints={{
					375: {
						slidesPerView: 1.7,
						spaceBetween: 10,
					},
					767: {
						slidesPerView: 2.8,
						spaceBetween: 30,
					},
					1024: {
						slidesPerView: 3.7,
						spaceBetween: 40,
					},
					1440: {
						slidesPerView: 3.7,
						spaceBetween: 40,
					},
				}}
			>
				{defined(items)
					.map(({ fields: { name, title, text, image } }, index) => (
						<SwiperSlide key={index}>
							<div className={styles.item}>
								{image?.fields.file && (
									<figure className={styles.slider__photo}>
										<Image
											src={assetUri(image)}
											alt={`Photo of ${name}`}
											height={200}
											width={200}
										/>
									</figure>
								)}
								<p className={styles['testimonial-name']} data-aos="fade-up">
									{' '}
									{name}
								</p>
								<div className={styles['testimonial-designation']} data-aos="fade-up">
									<RichText document={title} />
								</div>
								<div data-aos="fade-up">
									<RichText document={text} />
								</div>
								<LogoVector className={styles.logo__layer} width={70} height={143} />
							</div>
						</SwiperSlide>
					))}
			</Swiper>
		</div>
	</section>
